// The buyer directory — precached repeat-buyer autofill.
//
// Why precached, and why this is easy to miss (docs/plan.md): autofill is only
// useful if it recognises someone met at a DIFFERENT show, very possibly
// captured on a DIFFERENT phone. The design mock reads the device's own lead
// array, which silently degrades to "only buyers I typed on this handset". So
// `GET /api/buyers/directory` returns a deduplicated list across every show and
// every user (including archived leads), and it downloads FIRST — a few hundred
// rows of text, negligible next to the photos.
//
// SEAM FOR LATER WAVES (capture form): lookupBuyers(prefix) reads the local
// copy, so autofill works in a dead zone.
import type { BuyerDirectoryEntry } from '@paradise/shared';

import { apiRequest } from '@/lib/api/http';
import { db, metaSet, type BuyerDirectoryRow } from '@/lib/db';

export type BuyerSyncResult = 'updated' | 'offline' | 'unauthenticated' | 'error';

function toRow(entry: BuyerDirectoryEntry): BuyerDirectoryRow {
  return { ...entry, companyKey: entry.company.trim().toLowerCase() };
}

// Monotonic request/apply tokens — the same guard the leads mirror uses
// (leads-mirror.ts). pullAll() fires this from several triggers (boot,
// reconnect, tab focus) that can overlap; without the token a stale response
// landing late could clobber a fresher directory until the next refresh
// happened to correct it.
let refreshSeq = 0;
let appliedSeq = 0;

/** Fetch the directory and FULL-REPLACE the local copy (same self-healing logic as the leads mirror). */
export async function refreshBuyerDirectory(): Promise<BuyerSyncResult> {
  const seq = ++refreshSeq;
  const result = await apiRequest<BuyerDirectoryEntry[]>('/api/buyers/directory');
  switch (result.kind) {
    case 'ok':
      if (seq < appliedSeq) {
        // A later-started refresh already landed — don't let this stale
        // response clobber it.
        return 'updated';
      }
      appliedSeq = seq;
      await db.transaction('rw', db.buyers, db.meta, async () => {
        await db.buyers.clear();
        await db.buyers.bulkPut(result.data.map(toRow));
        await metaSet('buyersSyncAt', Date.now());
      });
      return 'updated';
    case 'offline':
      return 'offline';
    case 'unauthenticated':
      return 'unauthenticated';
    case 'error':
      return 'error';
  }
}

/**
 * Autofill lookup over the local directory. Case-insensitive prefix match on
 * company; capped so a long list stays cheap on a phone.
 */
export async function lookupBuyers(prefix: string, limit = 8): Promise<BuyerDirectoryRow[]> {
  const needle = prefix.trim().toLowerCase();
  if (needle.length === 0) return [];
  return db.buyers.where('companyKey').startsWith(needle).limit(limit).toArray();
}
